import React, { FC, FormEvent, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { greys, mainColors } from '../../../../../styling/theme';
import { ClassNameMap } from '@mui/styles';
import {
  UserAuthPreferencesData,
  getAuthMessageColor,
} from '../SecuritySettings.component';
import TokenService from '../../../../../services/token-service';
import FastAPIInstance from '../../../../../services/risksystem3-public-api';

const useStyles = makeStyles(() => ({
  settingsContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    padding: '0.62rem',
    borderRadius: '0.25rem',
  },
  settingsTitle: {
    fontSize: '1.25rem',
    borderBottom: `1px solid ${mainColors.mainBlue}`,
    width: 'fit-content',
    color: mainColors.mainBlue,
    fontWeight: 600,
    marginBottom: '0.62rem',
  },
  settingsSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.62rem',
    padding: '0.62rem',
  },
  settingsItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.62rem',
  },
  settingsItemTitle: {
    fontSize: '0.94rem',
    borderBottom: `1px solid ${mainColors.mainBlue}`,
    width: 'fit-content',
    color: mainColors.mainBlue,
    fontWeight: 600,
  },
  settingsItemDescription: {
    fontSize: '0.75rem',
    color: mainColors.mainBlue,
    fontWeight: 400,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.62rem',
  },
  settingsItemValueContainer: {
    display: 'flex',
    gap: '0.62rem',
    color: mainColors.mainBlue,
    fontWeight: 600,
    alignItems: 'center',
    fontSize: '1.00rem',
  },
  settingsItemValue: {
    all: 'unset',
    fontSize: '1.00rem',
    fontWeight: 400,
    padding: '0.31rem 0.62rem',
    color: mainColors.mainBlue,
    border: `1px solid ${mainColors.mainBlue}`,
    width: '6.25rem',
  },
  submitChangeContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.62rem',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '1.88rem',
  },
  submitEditButton: {
    all: 'unset',
    width: 'fit-content',
    display: 'flex',
    flexDirection: 'row',
    gap: '0.62rem',
    padding: '0.31rem 0.62rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '0.94rem',
    backgroundColor: mainColors.mainBlue,
    color: 'white',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: mainColors.mainBlue_lighter,
    },
    '&:active': {
      backgroundColor: mainColors.mainBlue_slightlyDarker,
    },
  },
  submitEditButton_inactive: {
    all: 'unset',
    width: 'fit-content',
    display: 'flex',
    flexDirection: 'row',
    gap: '0.62rem',
    padding: '0.31rem 0.62rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '0.94rem',
    backgroundColor: greys.grey300,
    color: 'white',
    userSelect: 'none',
  },
}));

// this is where the data will be passed in from the parent component
// be sure to also update the "No settings to display" message to include any changes to these props
interface Props {
  maximum_failed_login_attempts: UserAuthPreferencesData | undefined;
  account_frozen_minutes: UserAuthPreferencesData | undefined;
  two_factor_auth: UserAuthPreferencesData | undefined;
  reloadSettings: () => void;
}

const LoginSettings: FC<Props> = ({
  maximum_failed_login_attempts,
  account_frozen_minutes,
  two_factor_auth,
  reloadSettings,
}) => {
  const classes: ClassNameMap<string> = useStyles();

  const [maximumFailedLoginAttempts, setMaximumFailedLoginAttempts] =
    React.useState<number | null>(
      maximum_failed_login_attempts
        ? (maximum_failed_login_attempts.attribute_value as unknown as number)
        : 10,
    );
  const [accountFrozenMinutes, setAccountFrozenMinutes] = React.useState<
    number | null
  >(
    account_frozen_minutes
      ? (account_frozen_minutes.attribute_value as unknown as number)
      : 10,
  );
  const [twoFactorAuth, setTwoFactorAuth] = React.useState<boolean>(
    two_factor_auth
      ? (two_factor_auth.attribute_value as unknown as boolean)
      : false,
  );

  useEffect(() => {
    if (
      maximum_failed_login_attempts &&
      (maximum_failed_login_attempts.attribute_value as unknown as number) !==
        maximumFailedLoginAttempts
    ) {
      setMaximumFailedLoginAttempts(
        maximum_failed_login_attempts.attribute_value as unknown as number,
      );
    }
    if (
      account_frozen_minutes &&
      (account_frozen_minutes.attribute_value as unknown as number) !==
        accountFrozenMinutes
    ) {
      setAccountFrozenMinutes(
        account_frozen_minutes.attribute_value as unknown as number,
      );
    }
    if (
      two_factor_auth &&
      (two_factor_auth.attribute_value as unknown as boolean) !== twoFactorAuth
    ) {
      setTwoFactorAuth(two_factor_auth.attribute_value as unknown as boolean);
    }
  }, [maximum_failed_login_attempts, account_frozen_minutes]);

  const [
    maximumFailedLoginAttemptsRequestStatus,
    setMaximumFailedLoginAttemptsRequestStatus,
  ] = React.useState<string>('idle');
  const [
    maximumFailedLoginAttemptsRequestMessage,
    setMaximumFailedLoginAttemptsRequestMessage,
  ] = React.useState<string>('');

  const [
    accountFrozenMinutesRequestStatus,
    setAccountFrozenMinutesRequestStatus,
  ] = React.useState<string>('idle');
  const [
    accountFrozenMinutesRequestMessage,
    setAccountFrozenMinutesRequestMessage,
  ] = React.useState<string>('');

  const [twoFactorAuthRequestStatus, setTwoFactorAuthRequestStatus] =
    React.useState<string>('idle');
  const [twoFactorAuthRequestMessage, setTwoFactorAuthRequestMessage] =
    React.useState<string>('');

  const handleChangeMaximumFailedLoginAttempts = (
    e: FormEvent<HTMLFormElement>,
  ): void => {
    e.preventDefault();
    const validatedAttempts =
      maximumFailedLoginAttempts === null || maximumFailedLoginAttempts < 1
        ? 1
        : maximumFailedLoginAttempts;
    setMaximumFailedLoginAttempts(validatedAttempts);

    setMaximumFailedLoginAttemptsRequestStatus('loading');
    setMaximumFailedLoginAttemptsRequestMessage(
      'Updating maximum failed login attempts...',
    );
    FastAPIInstance.put('/v1/settings/auth_pref_attrs', {
      client_id: TokenService.getUser().user_client_id,
      attribute_name: 'maximum_failed_login_attempts',
      attributes: {
        attribute_value: validatedAttempts,
      },
    })
      .then((response) => {
        if (response.status === 200) {
          setMaximumFailedLoginAttemptsRequestStatus('success');
          setMaximumFailedLoginAttemptsRequestMessage('Updated successfully.');
          reloadSettings();
        } else {
          setMaximumFailedLoginAttemptsRequestStatus('fail');
          setMaximumFailedLoginAttemptsRequestMessage(response.data.detail);
        }
      })
      .catch((error) => {
        setMaximumFailedLoginAttemptsRequestStatus('fail');
        setMaximumFailedLoginAttemptsRequestMessage(
          error.response?.data?.detail ||
            'An error occurred while updating the maximum failed login attempts',
        );
      })
      .finally(() => {
        setTimeout(() => {
          setMaximumFailedLoginAttemptsRequestStatus('idle');
          setMaximumFailedLoginAttemptsRequestMessage('');
        }, 5000);
      });
  };

  const handleChangeAccountFrozenMinutes = (
    e: FormEvent<HTMLFormElement>,
  ): void => {
    e.preventDefault();
    const validatedMinutes =
      accountFrozenMinutes === null || accountFrozenMinutes < 1
        ? 1
        : accountFrozenMinutes;
    setAccountFrozenMinutes(validatedMinutes);

    setAccountFrozenMinutesRequestStatus('loading');
    setAccountFrozenMinutesRequestMessage(
      'Updating account frozen duration...',
    );
    FastAPIInstance.put('/v1/settings/auth_pref_attrs', {
      client_id: TokenService.getUser().user_client_id,
      attribute_name: 'account_frozen_minutes',
      attributes: {
        attribute_value: validatedMinutes,
      },
    })
      .then((response) => {
        if (response.status === 200) {
          setAccountFrozenMinutesRequestStatus('success');
          setAccountFrozenMinutesRequestMessage('Updated successfully.');
          reloadSettings();
        } else {
          setAccountFrozenMinutesRequestStatus('fail');
          setAccountFrozenMinutesRequestMessage(response.data.detail);
        }
      })
      .catch((error) => {
        setAccountFrozenMinutesRequestStatus('fail');
        setAccountFrozenMinutesRequestMessage(
          error.response?.data?.detail ||
            'An error occurred while updating the account frozen duration',
        );
      })
      .finally(() => {
        setTimeout(() => {
          setAccountFrozenMinutesRequestStatus('idle');
          setAccountFrozenMinutesRequestMessage('');
        }, 5000);
      });
  };

  const handleChangeTwoFactorAuth = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setTwoFactorAuthRequestStatus('loading');
    setTwoFactorAuthRequestMessage('Updating two factor authentication...');
    FastAPIInstance.put('/v1/settings/auth_pref_attrs', {
      client_id: TokenService.getUser().user_client_id,
      attribute_name: '2fa_user_email',
      attributes: {
        attribute_value: twoFactorAuth,
      },
    })
      .then((response) => {
        if (response.status === 200) {
          setTwoFactorAuthRequestStatus('success');
          setTwoFactorAuthRequestMessage('Updated successfully.');
          reloadSettings();
        } else {
          setTwoFactorAuthRequestStatus('fail');
          setTwoFactorAuthRequestMessage(response.data.detail);
        }
      })
      .catch((error) => {
        setTwoFactorAuthRequestStatus('fail');
        setTwoFactorAuthRequestMessage(
          error.response?.data?.detail ||
            'An error occurred while updating two factor authentication',
        );
      })
      .finally(() => {
        setTimeout(() => {
          setTwoFactorAuthRequestStatus('idle');
          setTwoFactorAuthRequestMessage('');
        }, 5000);
      });
  };

  return (
    <div className={classes.settingsContainer}>
      <div className={classes.settingsTitle}>Login Settings</div>
      {!maximum_failed_login_attempts && !account_frozen_minutes ? (
        <div className={classes.settingsSection}>
          <div className={classes.settingsItem}>
            <div className={classes.settingsItemDescription}>
              No settings to display
            </div>
          </div>
        </div>
      ) : null}
      {maximum_failed_login_attempts ? (
        <div className={classes.settingsSection}>
          <div className={classes.settingsItem}>
            <div className={classes.settingsItemTitle}>
              Maximum Failed Login Attempts
            </div>
            <div className={classes.settingsItemDescription}>
              The number of times a user can fail to login before their account
              is locked. This is a security measure to prevent brute force
              attacks.
            </div>
            <form
              className={classes.formContainer}
              onSubmit={(e: FormEvent<HTMLFormElement>) =>
                handleChangeMaximumFailedLoginAttempts(e)
              }
            >
              <div className={classes.settingsItemValueContainer}>
                <input
                  type="number"
                  min={1}
                  max={50}
                  step={1}
                  value={maximumFailedLoginAttempts ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    setMaximumFailedLoginAttempts(
                      value === '' ? null : Number(value),
                    );
                  }}
                  className={classes.settingsItemValue}
                />
                Attempts
              </div>
              <div className={classes.submitChangeContainer}>
                {(maximum_failed_login_attempts.attribute_value as unknown as number) !==
                maximumFailedLoginAttempts ? (
                  <button type="submit" className={classes.submitEditButton}>
                    Submit Change
                  </button>
                ) : (
                  <div className={classes.submitEditButton_inactive}>
                    Submit Change
                  </div>
                )}
                {maximumFailedLoginAttemptsRequestStatus != 'idle' ? (
                  <div
                    style={{
                      color: getAuthMessageColor(
                        maximumFailedLoginAttemptsRequestStatus,
                      ),
                    }}
                  >
                    <h3>{maximumFailedLoginAttemptsRequestMessage}</h3>
                  </div>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      ) : null}
      {account_frozen_minutes ? (
        <div className={classes.settingsSection}>
          <div className={classes.settingsItem}>
            <div className={classes.settingsItemTitle}>
              Account Frozen Duration
            </div>
            <div className={classes.settingsItemDescription}>
              The time in minutes that a user's account will be frozen for after
              exceeding the maximum failed login attempts. This is to prevent
              brute force attacks. We recommend atleast 2 minutes.
            </div>
            <form
              className={classes.formContainer}
              onSubmit={(e: FormEvent<HTMLFormElement>) =>
                handleChangeAccountFrozenMinutes(e)
              }
            >
              <div className={classes.settingsItemValueContainer}>
                <input
                  type="number"
                  min={1}
                  max={120}
                  step={1}
                  value={accountFrozenMinutes ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    setAccountFrozenMinutes(
                      value === '' ? null : Number(value),
                    );
                  }}
                  className={classes.settingsItemValue}
                />
                Minutes
              </div>
              <div className={classes.submitChangeContainer}>
                {(account_frozen_minutes.attribute_value as unknown as number) !==
                accountFrozenMinutes ? (
                  <button type="submit" className={classes.submitEditButton}>
                    Submit Change
                  </button>
                ) : (
                  <div className={classes.submitEditButton_inactive}>
                    Submit Change
                  </div>
                )}
                {accountFrozenMinutesRequestStatus != 'idle' ? (
                  <div
                    style={{
                      color: getAuthMessageColor(
                        accountFrozenMinutesRequestStatus,
                      ),
                    }}
                  >
                    <h3>{accountFrozenMinutesRequestMessage}</h3>
                  </div>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      ) : null}
      {two_factor_auth ? (
        <div className={classes.settingsSection}>
          <div className={classes.settingsItem}>
            <div className={classes.settingsItemTitle}>
              Two Factor Authentication (2FA)
            </div>
            <div className={classes.settingsItemDescription}>
              An extra layer of security that requires a verification code sent
              to your email, in addition to your password.
            </div>
            <div className={classes.settingsItemDescription}>
              This is an organisation wide setting. If you enable this, all
              users will be required to use 2FA.
            </div>
            {/* add a toggle here to turn 2FA on / off */}
            <form
              className={classes.formContainer}
              onSubmit={(e: FormEvent<HTMLFormElement>) =>
                handleChangeTwoFactorAuth(e)
              }
            >
              <div className={classes.settingsItemValueContainer}>
                <input
                  type="checkbox"
                  checked={twoFactorAuth}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setTwoFactorAuth(e.target.checked)
                  }
                />
                Enable 2FA Email Verification
              </div>
              <div className={classes.submitChangeContainer}>
                {(two_factor_auth?.attribute_value as unknown as boolean) !==
                twoFactorAuth ? (
                  <button type="submit" className={classes.submitEditButton}>
                    Submit Change
                  </button>
                ) : (
                  <div className={classes.submitEditButton_inactive}>
                    Submit Change
                  </div>
                )}
                {twoFactorAuthRequestStatus != 'idle' ? (
                  <div
                    style={{
                      color: getAuthMessageColor(twoFactorAuthRequestStatus),
                    }}
                  >
                    <h3>{twoFactorAuthRequestMessage}</h3>
                  </div>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LoginSettings;
